export type PortalPlanningTaskStatus = 'done' | 'inProgress' | 'planned' | 'notStarted';

export type PortalPlanningTask = {
  id: string;
  title: string;
  ownerName: string;
  startDate: string;
  endDate: string;
  progressPercent: number;
  status: PortalPlanningTaskStatus;
  dependsOnId?: string;
  isMilestone?: boolean;
};

export type PortalPlanningPhase = {
  id: string;
  number: number;
  title: string;
  progressPercent: number;
  startDate: string;
  endDate: string;
  status: PortalPlanningTaskStatus;
  tasks: PortalPlanningTask[];
};

export type PortalPlanningMilestone = {
  id: string;
  title: string;
  date: string;
  dateLabel: string;
  status: 'done' | 'current' | 'pending';
  description?: string;
};

export type PortalPlanningData = {
  projectId: string;
  periodStartLabel: string;
  periodEndLabel: string;
  timelineStart: string;
  timelineEnd: string;
  todayDate: string;
  progressPercent: number;
  progressDeltaLabel: string;
  phases: PortalPlanningPhase[];
  milestones: PortalPlanningMilestone[];
};

export const portalPlanningByProjectId: Record<string, PortalPlanningData> = {
  crm: {
    projectId: 'crm',
    periodStartLabel: '01 de set. de 2026',
    periodEndLabel: '15 de dez. de 2026',
    timelineStart: '2026-09-01',
    timelineEnd: '2026-12-15',
    todayDate: '2026-10-27',
    progressPercent: 68,
    progressDeltaLabel: '+12% desde o último mês',
    phases: [
      {
        id: 'phase-planning',
        number: 1,
        title: 'Planejamento',
        progressPercent: 100,
        startDate: '2026-09-01',
        endDate: '2026-09-15',
        status: 'done',
        tasks: [
          {
            id: 'task-kickoff',
            title: 'Kick-off do projeto',
            ownerName: 'Mariana Silva',
            startDate: '2026-09-01',
            endDate: '2026-09-03',
            progressPercent: 100,
            status: 'done',
          },
          {
            id: 'task-requirements',
            title: 'Levantamento de requisitos',
            ownerName: 'Rafael Oliveira',
            startDate: '2026-09-04',
            endDate: '2026-09-10',
            progressPercent: 100,
            status: 'done',
            dependsOnId: 'task-kickoff',
          },
          {
            id: 'task-solution',
            title: 'Definição da solução',
            ownerName: 'Mariana Silva',
            startDate: '2026-09-11',
            endDate: '2026-09-15',
            progressPercent: 100,
            status: 'done',
            dependsOnId: 'task-requirements',
          },
        ],
      },
      {
        id: 'phase-configuration',
        number: 2,
        title: 'Configuração',
        progressPercent: 100,
        startDate: '2026-09-16',
        endDate: '2026-10-10',
        status: 'done',
        tasks: [
          {
            id: 'task-environment',
            title: 'Configuração do ambiente',
            ownerName: 'Carlos Mendes',
            startDate: '2026-09-16',
            endDate: '2026-09-25',
            progressPercent: 100,
            status: 'done',
            dependsOnId: 'task-solution',
          },
          {
            id: 'task-params',
            title: 'Parametrizações iniciais',
            ownerName: 'Ana Souza',
            startDate: '2026-09-26',
            endDate: '2026-10-10',
            progressPercent: 100,
            status: 'done',
            dependsOnId: 'task-environment',
          },
        ],
      },
      {
        id: 'phase-integration',
        number: 3,
        title: 'Integração',
        progressPercent: 60,
        startDate: '2026-10-11',
        endDate: '2026-11-05',
        status: 'inProgress',
        tasks: [
          {
            id: 'task-erp',
            title: 'Integração com ERP',
            ownerName: 'Carlos Mendes',
            startDate: '2026-10-11',
            endDate: '2026-10-31',
            progressPercent: 60,
            status: 'inProgress',
            dependsOnId: 'task-params',
          },
          {
            id: 'task-tools',
            title: 'Integração com outras ferramentas',
            ownerName: 'Rafael Oliveira',
            startDate: '2026-10-20',
            endDate: '2026-11-05',
            progressPercent: 30,
            status: 'inProgress',
            dependsOnId: 'task-erp',
          },
        ],
      },
      {
        id: 'phase-testing',
        number: 4,
        title: 'Testes e Homologação',
        progressPercent: 0,
        startDate: '2026-11-06',
        endDate: '2026-12-05',
        status: 'planned',
        tasks: [
          {
            id: 'task-tests',
            title: 'Execução dos testes',
            ownerName: 'Ana Souza',
            startDate: '2026-11-06',
            endDate: '2026-11-20',
            progressPercent: 0,
            status: 'planned',
            dependsOnId: 'task-tools',
          },
          {
            id: 'task-uat',
            title: 'Homologação com o cliente',
            ownerName: 'Mariana Silva',
            startDate: '2026-11-21',
            endDate: '2026-12-05',
            progressPercent: 0,
            status: 'planned',
            dependsOnId: 'task-tests',
            isMilestone: true,
          },
        ],
      },
      {
        id: 'phase-go-live',
        number: 5,
        title: 'Implantação',
        progressPercent: 0,
        startDate: '2026-12-06',
        endDate: '2026-12-15',
        status: 'notStarted',
        tasks: [
          {
            id: 'task-training',
            title: 'Treinamento da equipe',
            ownerName: 'Ana Souza',
            startDate: '2026-12-06',
            endDate: '2026-12-10',
            progressPercent: 0,
            status: 'notStarted',
            dependsOnId: 'task-uat',
          },
          {
            id: 'task-golive',
            title: 'Go-live',
            ownerName: 'Mariana Silva',
            startDate: '2026-12-11',
            endDate: '2026-12-15',
            progressPercent: 0,
            status: 'notStarted',
            dependsOnId: 'task-training',
            isMilestone: true,
          },
        ],
      },
    ],
    milestones: [
      {
        id: 'ms-planning',
        title: 'Planejamento concluído',
        date: '2026-09-15',
        dateLabel: '15/09',
        status: 'done',
        description: 'Kick-off, requisitos e definição da solução finalizados.',
      },
      {
        id: 'ms-environment',
        title: 'Ambiente configurado',
        date: '2026-10-10',
        dateLabel: '10/10',
        status: 'done',
        description: 'Ambiente e parametrizações iniciais disponíveis para o time.',
      },
      {
        id: 'ms-erp',
        title: 'Integração com ERP',
        date: '2026-10-31',
        dateLabel: '31/10',
        status: 'current',
        description: 'Conexão com o ERP em andamento, com validação parcial.',
      },
      {
        id: 'ms-uat',
        title: 'Homologação',
        date: '2026-12-05',
        dateLabel: '05/12',
        status: 'pending',
        description: 'Aceite formal das funcionalidades com a equipe do cliente.',
      },
      {
        id: 'ms-golive',
        title: 'Go-live',
        date: '2026-12-15',
        dateLabel: '15/12',
        status: 'pending',
        description: 'Entrada em produção e capacitação da operação.',
      },
    ],
  },
};
